import { Button, type ButtonProps } from '@affine/component/ui/button';
import { Tooltip } from '@affine/component/ui/tooltip';
import { generateSubscriptionCallbackLink } from '@affine/core/components/hooks/affine/use-subscription-notify';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import {
  AuthService,
  ServerService,
  SubscriptionService,
} from '@affine/core/modules/cloud';
import { GlobalDialogService } from '@affine/core/modules/dialogs';
import { UrlService } from '@affine/core/modules/url';
import {
  type CreateCheckoutSessionInput,
  SubscriptionPlan,
  SubscriptionRecurring,
  SubscriptionStatus,
  SubscriptionVariant,
} from '@affine/graphql';
import { Trans, useI18n, i18nTime } from '@affine/i18n';
import { track } from '@affine/track';
import { DoneIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import type { PropsWithChildren } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { CancelAction, ResumeAction } from './actions';
import { CheckoutSlot } from '../../../../components/checkout-slot-new';
import type { DynamicPrice, FixedPrice } from './cloud-plans';
import { ConfirmLoadingModal } from './modals';
import * as styles from './style.css';
import IconFont from '@ys/repo-ui/base/IconFont';
import { I18nService } from '@affine/core/modules/i18n';
interface PlanCardProps {
  detail: FixedPrice | DynamicPrice;
  recurring: SubscriptionRecurring;
  isShowBlackFriday?: boolean;
}

export const PlanCard = (props: PlanCardProps) => {
  const { detail, recurring, isShowBlackFriday } = props;
  const loggedIn =
    useLiveData(useService(AuthService).session.status$) === 'authenticated';
  const subscriptionService = useService(SubscriptionService);
  const proSubscription = useLiveData(subscriptionService.subscription.pro$);
  const studioSubscription = useLiveData(
    subscriptionService.subscription.studio$
  );
  const expertSubscription = useLiveData(
    subscriptionService.subscription.expert$
  );

  const currentPlan = useLiveData(
    subscriptionService.subscription.currentPlan$
  );

  const shouldShowBlackFriday = useMemo(() => {
    if (
      isShowBlackFriday &&
      ((detail.plan === SubscriptionPlan.Pro &&
        recurring === SubscriptionRecurring.Yearly) ||
        detail.plan === SubscriptionPlan.Studio)
    ) {
      return true;
    }
    return false;
  }, [isShowBlackFriday, recurring, detail]);
  const t = useI18n();

  const isCurrent = useMemo(() => {
    let userRecurring;
    if (currentPlan === SubscriptionPlan.Studio) {
      userRecurring = studioSubscription?.recurring;
    } else if (currentPlan === SubscriptionPlan.Expert) {
      userRecurring = expertSubscription?.recurring;
    } else if (currentPlan === SubscriptionPlan.Pro) {
      userRecurring = proSubscription?.recurring;
    }
    return (
      loggedIn && detail.plan === currentPlan && recurring === userRecurring
    );
  }, [
    loggedIn,
    detail.plan,
    currentPlan,
    recurring,
    proSubscription,
    studioSubscription,
    expertSubscription,
  ]);

  return (
    <div data-current={isCurrent} key={detail.plan} className={styles.planCard}>
      {/* <div className={styles.planCardBorderMock} /> */}
      <div className={styles.planTitle}>
        <div style={{ paddingBottom: 24, width: '284px' }}>
          <section className={styles.planTitleName}>{detail.name}</section>
          <section className={styles.planTitleDescription}>
            {detail.description}
          </section>
          <section className={styles.planTitleTitle}>
            {detail.titleRenderer(recurring, detail as any)}
          </section>
          {shouldShowBlackFriday && (
            <div className={styles.planTitleTag}>
              {t['com.affine.payment.cloud.pricing-plan.black-friday']()}
            </div>
          )}
        </div>

        <ActionButton {...props} />
      </div>
      <div className={styles.planBenefits}>
        {Object.entries(detail.benefits).map(([groupName, benefitList]) => {
          return (
            <ul className={styles.planBenefitGroup} key={groupName}>
              <section className={styles.planBenefitGroupTitle}>
                {groupName}
              </section>
              {benefitList.map(({ icon, title, badge }, index) => {
                return (
                  <li className={styles.planBenefit} key={index}>
                    <div className={styles.planBenefitIcon}>
                      {icon ?? (
                        <IconFont
                          className={styles.planBenefitIconImage}
                          name="checkIcon"
                          size={24}
                        />
                      )}
                    </div>
                    <div className={styles.planBenefitText}>
                      {title}
                      {badge && (
                        <span className={styles.planBenefitTag}>{badge}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          );
        })}
      </div>
    </div>
  );
};

const getSignUpText = (
  plan: SubscriptionPlan,
  t: ReturnType<typeof useI18n>
) => {
  switch (plan) {
    case SubscriptionPlan.Free:
      return t['com.affine.payment.sign-up-free']();
    case SubscriptionPlan.Team:
      return t['com.affine.payment.upgrade']();
    default:
      return t['com.affine.payment.buy-pro']();
  }
};

const ActionButton = ({ detail, recurring }: PlanCardProps) => {
  const t = useI18n();
  const loggedIn =
    useLiveData(useService(AuthService).session.status$) === 'authenticated';
  const subscriptionService = useService(SubscriptionService);
  const isBeliever = useLiveData(subscriptionService.subscription.isBeliever$);
  const primarySubscription = useLiveData(
    subscriptionService.subscription.pro$
  );
  const studioSubscription = useLiveData(
    subscriptionService.subscription.studio$
  );
  const expertSubscription = useLiveData(
    subscriptionService.subscription.expert$
  );
  const currentPlan = useLiveData(
    subscriptionService.subscription.currentPlan$
  );
  const currentRecurring =
    studioSubscription?.recurring ??
    expertSubscription?.recurring ??
    primarySubscription?.recurring;
  const isOnetime = useLiveData(subscriptionService.subscription.isOnetimePro$);
  const isFree = detail.plan === SubscriptionPlan.Free;

  const signUpText = useMemo(
    () => getSignUpText(detail.plan, t),
    [detail.plan, t]
  );

  // branches:
  //  if contact                                => 'Contact Sales'
  //  if not signed in:
  //    if free                                 => 'Sign up free'
  //    if team                                 => 'Upgrade'
  //    else                                    => 'Buy Pro'
  //  else
  //    if team                                 => 'Start 14-day free trial'
  //    if isBeliever                           => 'Included in Lifetime'
  //    if onetime
  //      if free                               => 'Included in Pro'
  //      else                                  => 'Redeem Code'
  //    if isCurrent
  //      if canceled                           => 'Resume'
  //      else                                  => 'Current Plan'
  //    if free                                 => 'Downgrade'
  //    if currentRecurring !== recurring       => 'Change to {recurring} Billing'
  //    else                                    => 'Upgrade'

  // team
  if (detail.plan === SubscriptionPlan.Team) {
    return <UpgradeToTeam recurring={recurring} />;
  }

  // not signed in
  if (!loggedIn) {
    return <SignUpAction>{signUpText}</SignUpAction>;
  }

  // lifetime
  if (isBeliever) {
    return (
      <Button className={styles.planAction} disabled>
        {t['com.affine.payment.cloud.lifetime.included']()}
      </Button>
    );
  }

  // onetime
  if (isOnetime) {
    return isFree ? (
      <Button className={styles.planAction} disabled>
        {t['com.affine.payment.cloud.onetime.included']()}
      </Button>
    ) : (
      <RedeemCode recurring={recurring} currentPlan={currentPlan} />
    );
  }

  const isCanceled = useMemo(() => {
    if (currentPlan === SubscriptionPlan.Studio) {
      return !!studioSubscription?.canceledAt;
    } else {
      return !!primarySubscription?.canceledAt;
    }
  }, [primarySubscription, studioSubscription, currentPlan]);
  const isCurrent = useMemo(() => {
    let active = false;
    if (currentPlan === SubscriptionPlan.Studio) {
      active = studioSubscription?.status === SubscriptionStatus.Active;
    } else if (currentPlan === SubscriptionPlan.Expert) {
      active = expertSubscription?.status === SubscriptionStatus.Active;
    } else if (currentPlan === SubscriptionPlan.Pro) {
      active = primarySubscription?.status === SubscriptionStatus.Active;
    }
    return (
      detail.plan === currentPlan &&
      (isFree ? true : currentRecurring === recurring && active)
    );
  }, [
    primarySubscription,
    studioSubscription,
    currentPlan,
    isFree,
    recurring,
    currentRecurring,
    expertSubscription,
  ]);

  // is current
  if (isCurrent) {
    return isCanceled ? <ResumeButton plan={detail.plan} /> : <CurrentPlan />;
  }

  if (isFree) {
    return <Downgrade disabled={isCanceled} />;
  }

  return currentPlan === detail.plan ? (
    <ChangeRecurring
      from={currentRecurring as SubscriptionRecurring}
      to={recurring as SubscriptionRecurring}
      due={
        studioSubscription?.end ??
        expertSubscription?.end ??
        primarySubscription?.end ??
        ''
      }
      currentPlan={currentPlan}
      isCanceled={isCanceled}
      detail={detail}
      // disabled={isCanceled}
    />
  ) : (
    <Upgrade
      currentPlan={currentPlan}
      recurring={recurring as SubscriptionRecurring}
      plan={detail.plan}
      detail={detail}
      className={styles.upgradePlan}
    />
  );
};

const CurrentPlan = () => {
  const t = useI18n();
  return (
    <Button className={`${styles.planAction} ${styles.currentPlan}`} disabled>
      {t['com.affine.payment.current-plan']()}
    </Button>
  );
};

const Downgrade = ({ disabled }: { disabled?: boolean }) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);

  const tooltipContent = disabled
    ? t['com.affine.payment.downgraded-tooltip']()
    : null;

  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <CancelAction open={open} onOpenChange={setOpen}>
      <Tooltip content={tooltipContent} rootOptions={{ delayDuration: 0 }}>
        <div className={styles.planAction}>
          <Button
            className={`${styles.planAction} ${styles.borderButton} ${disabled ? styles.borderButtonDisabled : ''}`}
            variant="primary"
            onClick={handleClick}
            disabled={disabled}
          >
            {t['com.affine.payment.downgrade']()}
          </Button>
        </div>
      </Tooltip>
    </CancelAction>
  );
};

const UpgradeToTeam = ({ recurring }: { recurring: SubscriptionRecurring }) => {
  const t = useI18n();
  const serverService = useService(ServerService);
  const urlService = useService(UrlService);
  const url = `${serverService.server.baseUrl}/upgrade-to-team?recurring=${recurring}`;
  const scheme = urlService.getClientScheme();
  const urlParams = new URLSearchParams();
  if (scheme) {
    urlParams.set('client', scheme);
  }

  return (
    <a
      className={styles.planAction}
      href={`${url}${urlParams.toString() ? `&${urlParams.toString()}` : ''}`}
      target="_blank"
      rel="noreferrer"
    >
      <Button
        className={styles.planAction}
        variant="primary"
        data-event-args-url={`${url}${urlParams.toString() ? `&${urlParams.toString()}` : ''}`}
      >
        {t['com.affine.payment.upgrade']()}
      </Button>
    </a>
  );
};

export const Upgrade = ({
  className,
  recurring,
  plan,
  currentPlan,
  workspaceId,
  children,
  checkoutInput,
  onCheckoutSuccess,
  onBeforeCheckout,
  detail,
  ...btnProps
}: ButtonProps & {
  recurring: SubscriptionRecurring;
  currentPlan: SubscriptionPlan;
  plan: SubscriptionPlan;
  workspaceId?: string;
  checkoutInput?: Partial<CreateCheckoutSessionInput>;
  detail?: FixedPrice | DynamicPrice;
  onBeforeCheckout?: () => void;
  onCheckoutSuccess?: () => void;
}) => {
  const t = useI18n();
  const authService = useService(AuthService);
  const urlService = useService(UrlService);
  const schema = urlService.getClientScheme();

  const handleBeforeCheckout = useCallback(() => {
    track.$.settingsPanel.plans.checkout({
      plan: plan,
      recurring: recurring,
    });
    onBeforeCheckout?.();
  }, [onBeforeCheckout, plan, recurring]);
  // const buttonIsDisabled = useMemo(() => {
  //   if (
  //     (currentPlan === SubscriptionPlan.Pro &&
  //       plan === SubscriptionPlan.Studio) ||
  //     (currentPlan === SubscriptionPlan.Studio && plan === SubscriptionPlan.Pro)
  //   ) {
  //     return true;
  //   }
  //   return false;
  // }, [currentPlan, plan]);

  const i18nKey = useMemo(() => {
    if (plan === SubscriptionPlan.Pro) {
      return 'com.affine.payment.pro.upgrade';
    }
    return 'com.affine.payment.studio.upgrade';
  }, [plan]);

  // const checkoutOptions = useMemo(
  //   () => ({
  //     recurring,
  //     plan: plan,
  //     successCallbackLink: generateSubscriptionCallbackLink(
  //       authService.session.account$.value,
  //       plan,
  //       recurring,
  //       workspaceId || '',
  //       schema
  //     ),
  //     price:
  //       recurring === SubscriptionRecurring.Yearly
  //         ? detail?.yearlyPrice
  //         : detail?.monthlyPrice,
  //     productId:
  //       recurring === SubscriptionRecurring.Yearly
  //         ? detail?.yearlyProductId
  //         : detail?.monthlyProductId,
  //     ...checkoutInput,
  //   }),
  //   [
  //     authService.session.account$.value,
  //     checkoutInput,
  //     plan,
  //     recurring,
  //     schema,
  //     workspaceId,
  //     detail,
  //   ]
  // );
  const checkoutOptions = useMemo(
    () => ({
      recurring,
      plan: plan,
      variant: null,
      coupon: null,
      successCallbackLink: generateSubscriptionCallbackLink(
        authService.session.account$.value,
        plan,
        recurring,
        workspaceId || '',
        schema
      ),
      ...checkoutInput,
    }),
    [
      authService.session.account$.value,
      checkoutInput,
      plan,
      recurring,
      schema,
      workspaceId,
    ]
  );

  return (
    <CheckoutSlot
      checkoutOptions={{
        productId:
          recurring === SubscriptionRecurring.Yearly
            ? detail!.yearlyProductId
            : detail!.monthlyProductId,
        price:
          recurring === SubscriptionRecurring.Yearly
            ? (detail!.yearlyPrice / 100).toFixed(2)
            : detail!.monthlySellingAmount,
        successCallbackLink: generateSubscriptionCallbackLink(
          authService.session.account$.value,
          plan,
          recurring,
          workspaceId || '',
          schema
        ),
      }}
      renderer={props => (
        <Button
          className={clsx(
            styles.planAction,
            className
            // buttonIsDisabled && styles.currentPlan
          )}
          // variant={!buttonIsDisabled ? 'primary' : undefined}
          variant="primary"
          {...props}
          {...btnProps}
          // disabled={buttonIsDisabled}
        >
          {children ?? t[`${i18nKey}`]()}
        </Button>
      )}
    />
  );
};

const ChangeRecurring = ({
  from,
  to,
  due,
  currentPlan,
  isCanceled,
  detail,
}: {
  from: SubscriptionRecurring;
  to: SubscriptionRecurring;
  disabled?: boolean;
  due: string;
  currentPlan: SubscriptionPlan;
  isCanceled: boolean;
  detail: FixedPrice | DynamicPrice;
}) => {
  const t = useI18n();
  const i18n = useService(I18nService).i18n;
  const currentLanguage = useLiveData(i18n.currentLanguage$);

  const [open, setOpen] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  // allow replay request on network error until component unmount or success
  const [idempotencyKey, setIdempotencyKey] = useState(nanoid());
  const subscription = useService(SubscriptionService).subscription;
  const dueTime = useMemo(() => {
    return i18nTime(new Date(due), {
      absolute: { accuracy: 'day' },
    });
  }, [due]);
  const fromText = useMemo(() => {
    if (currentLanguage.key === 'en') {
      return from;
    } else {
      return from === 'Monthly' ? '月度' : '年度';
    }
  }, [currentLanguage, from]);
  const toText = useMemo(() => {
    if (currentLanguage.key === 'en') {
      return to;
    } else {
      return to === 'Monthly' ? '月度' : '年度';
    }
  }, [currentLanguage, to]);

  const changeToText = useMemo(() => {
    if (currentLanguage.key === 'en') {
      return t['com.affine.payment.change-to']({ to });
    } else {
      return t['com.affine.payment.change-to']({
        to: to === 'Monthly' ? '月度' : '年度',
      });
    }
  }, [currentLanguage, t, to]);

  const onStartChange = useCallback(() => {
    track.$.settingsPanel.plans.changeSubscriptionRecurring({
      plan: SubscriptionPlan.Pro,
      recurring: to,
    });
    setOpen(true);
  }, [to]);

  const change = useAsyncCallback(async () => {
    setIsMutating(true);
    if (currentPlan === detail.plan && isCanceled) {
      await subscription.resumeSubscription(idempotencyKey, currentPlan);
      await subscription.waitForRevalidation();
      // refresh idempotency key
      setIdempotencyKey(nanoid());

      const proSubscription = subscription.pro$.value;
      const studioSubscription = subscription.studio$.value;
      const expertSubscription = subscription.expert$.value;
      if (studioSubscription || expertSubscription || proSubscription) {
        await subscription.setSubscriptionRecurring(
          idempotencyKey,
          to,
          currentPlan
        );
        setIdempotencyKey(nanoid());
      }
    } else {
      await subscription.setSubscriptionRecurring(
        idempotencyKey,
        to,
        currentPlan
      );
      setIdempotencyKey(nanoid());
    }
    setIsMutating(false);
  }, [subscription, to, idempotencyKey, isCanceled, detail.plan, currentPlan]);

  const changeCurringContent = (
    <Trans
      i18nKey={'com.affine.payment.change.modal.content'}
      values={{ from: fromText, to: toText, due: dueTime }}
      className={styles.downgradeContent}
    >
      You are changing your <span className={styles.textEmphasis}>{from} </span>
      subscription to <span className={styles.textEmphasis}>{to} </span>
      subscription. This change will take effect in the next billing cycle, with
      an effective date of
      <span className={styles.textEmphasis}> {dueTime}</span>.
    </Trans>
  );

  return (
    <>
      <Button
        className={`${styles.planAction} ${styles.borderButton}`}
        variant="primary"
        onClick={onStartChange}
        disabled={isMutating}
        loading={isMutating}
      >
        {changeToText}
      </Button>

      <ConfirmLoadingModal
        type={'change'}
        loading={isMutating}
        open={open}
        onConfirm={change}
        onOpenChange={setOpen}
        content={changeCurringContent}
      />
    </>
  );
};

export const SignUpAction = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  const globalDialogService = useService(GlobalDialogService);

  const onClickSignIn = useCallback(() => {
    globalDialogService.open('sign-in', {});
  }, [globalDialogService]);

  return (
    <Button
      onClick={onClickSignIn}
      className={clsx(styles.planAction, className)}
      variant="primary"
    >
      {children}
    </Button>
  );
};

const ResumeButton = ({ plan }: { plan: string }) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <ResumeAction open={open} onOpenChange={setOpen} plan={plan}>
      <Button className={styles.resumeAction} onClick={handleClick}>
        <span data-show-hover="true" className={clsx(styles.resumeContent)}>
          {t['com.affine.payment.resume-renewal']()}
        </span>
        <span data-show-hover="false" className={clsx(styles.resumeContent)}>
          {t['com.affine.payment.current-plan']()}
        </span>
      </Button>
    </ResumeAction>
  );
};

const redeemCodeCheckoutInput = { variant: SubscriptionVariant.Onetime };
export const RedeemCode = ({
  className,
  recurring = SubscriptionRecurring.Yearly,
  plan,
  currentPlan,
  children,
  ...btnProps
}: ButtonProps & {
  recurring?: SubscriptionRecurring;
  plan?: SubscriptionPlan;
  currentPlan: SubscriptionPlan;
}) => {
  const t = useI18n();

  return (
    <Upgrade
      recurring={recurring}
      className={className}
      currentPlan={currentPlan}
      checkoutInput={redeemCodeCheckoutInput}
      plan={plan ?? SubscriptionPlan.Pro}
      {...btnProps}
    >
      {children ?? t['com.affine.payment.redeem-code']()}
    </Upgrade>
  );
};
